import { useEffect } from 'react';

/**
 * A host-owned landing spot for a Trellus (dialer extension) control.
 *
 * The extension injects its own DOM into this `div`. So it renders the three
 * `data-trellus-clay-*` attributes and NOTHING else: no children, ever (React
 * must not own an extension-owned subtree), and it is never conditionally
 * unmounted (a missing slot makes the extension fall back to scraping the page).
 *
 * Every rep gets the slot (no Terra Beta gate): what shows is decided by the
 * extension, which only fills a slot when it advertises that capability
 * (`contacts_scoped`, source advertised, …). The slot is never hidden or unmounted.
 *
 * Extension absent: an empty div with no box (`contents`, or an empty block) —
 * zero layout shift. Attributes are props, so recycled virtualized rows stay current.
 */
export type TrellusSlotKind = 'bulk-dial' | 'gong-connection' | 'sidebar' | 'dial';

export interface TrellusSlotProps {
  kind: TrellusSlotKind;
  /** `dial` only: `signal_action:…` for a Signal, `clay_contact:<id>` for a contact. */
  recordId?: string;
  /** `dial` only. */
  dialKind?: 'row' | 'detail';
  className?: string;
}

/** Kinds that must exist exactly once per document (the extension ignores ambiguous singletons). */
const SINGLETON_KINDS: ReadonlySet<TrellusSlotKind> = new Set(['bulk-dial', 'gong-connection', 'sidebar']);
const mountedSingletons = new Map<TrellusSlotKind, number>();

/** Dev-only: warn when a singleton slot is mounted twice (the extension then falls back for both). */
function useSingletonGuard(kind: TrellusSlotKind): void {
  useEffect(() => {
    if (!import.meta.env.DEV || !SINGLETON_KINDS.has(kind)) return;
    const next = (mountedSingletons.get(kind) ?? 0) + 1;
    mountedSingletons.set(kind, next);
    if (next > 1) {
      // oxlint-disable-next-line no-console -- dev-only misuse warning
      console.warn(`[trellus] ${next} "${kind}" slots are mounted; the extension needs exactly one per document.`);
    }
    return () => {
      mountedSingletons.set(kind, (mountedSingletons.get(kind) ?? 1) - 1);
    };
  }, [kind]);
}

export function TrellusSlot({ kind, recordId, dialKind, className }: TrellusSlotProps) {
  useSingletonGuard(kind);
  // `contents`: an empty inline slot must not add a flex gap beside its siblings.
  const base = kind === 'dial' || kind === 'bulk-dial' ? 'contents' : '';
  const classes = [base, className].filter(Boolean).join(' ');
  return (
    <div
      data-trellus-clay-slot={kind}
      data-trellus-clay-record-id={kind === 'dial' ? recordId : undefined}
      data-trellus-clay-dial-kind={kind === 'dial' ? dialKind : undefined}
      className={classes || undefined}
    />
  );
}
